import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BuildingView } from '@api-proxies';
import { BuildingService } from '@http/clients/building.service';
import { EntityTypes } from '@models/enums/EntityTypes';
import { TabNames } from '@models/enums/TabNames';
import { AwaiterService } from '@services/awaiter.service';
import { TabCounterService, TabCounterType } from '@services/tab-counter.service';
import { ActivityLogGridComponent } from '@shared/activity-log-grid/activity-log-grid.component';
import { KebabMenuComponent } from '@shared/kebab-menu/kebab-menu.component';
import { ModalConfirmComponent } from '@shared/modals/modal-confirm/modal-confirm.component';
import { NotesComponent } from '@shared/notes/notes.component';
import { SectionHeaderComponent } from '@shared/section-header/section-header.component';
import { getTab, processTabParameter } from '@utils/tab-navigation';
import {
    CounterBadgeComponent,
    ModalService,
    StatusIndicatorComponent,
    TabItemComponent,
    TabViewComponent
} from 'ngx-lib';
import { Subscription } from 'rxjs';
import { BuildingsSingleCompaniesComponent } from './buildings-single-companies/buildings-single-companies.component';
import { BuildingsSingleContactsComponent } from './buildings-single-contacts/buildings-single-contacts.component';
import { BuildingsSingleOpportunitiesComponent } from './buildings-single-opportunities/buildings-single-opportunities.component';
import { BuildingsSingleOverviewComponent } from './buildings-single-overview/buildings-single-overview.component';

@Component({
    selector: 'app-buildings-single',
    templateUrl: './buildings-single.component.html',
    styleUrls: ['./buildings-single.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SectionHeaderComponent,
        StatusIndicatorComponent,
        TabViewComponent,
        TabItemComponent,
        CounterBadgeComponent,
        KebabMenuComponent,
        BuildingsSingleOverviewComponent,
        BuildingsSingleOpportunitiesComponent,
        BuildingsSingleContactsComponent,
        BuildingsSingleCompaniesComponent,
        NotesComponent,
        ActivityLogGridComponent
    ]
})
export class BuildingsSingleComponent implements OnInit, OnDestroy {
    @ViewChild(TabViewComponent)
    tabControl!: TabViewComponent;

    buildingId: number;

    building?: BuildingView;

    currentTab: string;

    isActive?: boolean;

    entityTypeId: EntityTypes;

    tabsCounters: TabCounterType = {
        Opportunities: 0,
        Contacts: 0,
        Companies: 0,
        Notes: 0
    };

    entitySubscription?: Subscription;

    constructor(
        private readonly buildingService: BuildingService,
        private readonly awaiter: AwaiterService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly modalService: ModalService,
        private readonly tabCounterService: TabCounterService
    ) {
        this.buildingId = parseInt(this.route.snapshot.paramMap.get('id') ?? '0');
        this.currentTab = getTab(this.route.snapshot.paramMap.get('tab')) ?? TabNames[TabNames.Overview];
        this.isActive = true;
        this.entityTypeId = EntityTypes.Building;
    }

    async ngOnInit(): Promise<void> {
        await this.updateTabCounters();

        this.entitySubscription = this.route.data.subscribe(async ({ entityData }) => {
            this.building = entityData;
            this.isActive = this.building?.isActive;
        });

        this.route.paramMap.subscribe(async (params) => {
            const newTab = getTab(params.get('tab'));

            if (newTab && newTab !== this.currentTab && this.tabControl) {
                this.currentTab = processTabParameter(this.route, this.tabControl);
            }
        });
    }

    ngOnDestroy(): void {
        if (this.entitySubscription) {
            this.entitySubscription?.unsubscribe();
        }
    }

    onEditBuilding() {
        this.router.navigate([`/buildings-edit/${this.buildingId}`]);
    }

    async archiveBuilding(): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Archive',
            cancelCaption: 'Cancel',
            content: 'Are you sure you want to archive this building?',
            title: 'Archive Building'
        });

        if (!responseOk) return;

        await this.awaiter.awaitAction('Archiving Building', async () => {
            if (!this.building?.id) return;

            await this.buildingService.deactivate(this.building?.id);
            this.isActive = false;
        });
    }

    async restoreBuilding(): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Restore',
            cancelCaption: 'Cancel',
            content: 'Are you sure you want to restore this building?',
            title: 'Restore Building'
        });

        if (!responseOk) return;

        await this.awaiter.awaitAction('Restoring Building', async () => {
            if (!this.building?.id) return;
            await this.buildingService.activate(this.building.id);
            this.isActive = true;
        });
    }

    async onTabCountersChange(): Promise<void> {
        await this.updateTabCounters();
    }

    tabChange(tabName: string) {
        this.router.navigate(['/buildings-single', this.buildingId, tabName]);
    }

    async handleBuildingChange(): Promise<void> {
        if (!this.buildingId) return;

        const res = await this.updateBuildingInfo();
        if (res) await this.updateTabCounters();
    }

    private async updateBuildingInfo(): Promise<boolean | undefined> {
        return await this.awaiter.awaitAction('Getting Building Info', async () => {
            this.building = await this.buildingService.getById(this.buildingId);
            this.isActive = this.building.isActive;
            return true;
        });
    }

    private async updateTabCounters(): Promise<void> {
        await this.tabCounterService.updateCounters(this.entityTypeId, this.buildingId, this.tabsCounters);
    }
}
